import { type ApiSchemas, call } from '@rental/contracts/client'
import { ALERT_KEYS, type AlertKey } from '@rental/domain'
import { format, formatDate, formatDateTime, formatTime } from '@rental/i18n'
import { Button, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { DataTable } from '../components/DataTable'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { PendingBadge, PendingLine } from '../components/PendingBadge'
import { Section } from '../components/Section'
import { StatusChip } from '../components/StatusChip'
import { Guard, LoadState } from '../components/states'
import { TechnicalDetails } from '../components/TechnicalDetails'
import { Facts, ProblemNotice } from '../components/ui'
import { useSend } from '../fines/send'
import { useLocale } from '../i18n'
import { noticeLines } from '../notices'
import { problemOf } from '../problems'
import { BookingCode } from '../reports/BookingCode'
import { PrivacySection } from './SystemPrivacy'

/** S2: the screen refreshes every minute; the banner asks every five (Shell). */
export const SYSTEM_REFRESH_MS = 60_000
export const SYSTEM_QUERY_KEY = ['system'] as const

export const loadSystem = () => load(call(api.GET('/v1/staff/system')))

const isAlertKey = (key: string): key is AlertKey => (ALERT_KEYS as readonly string[]).includes(key)

/**
 * S2: the Owner's view of what runs behind the app: backups, the background
 * service, the messages it could not send, the alerts and who receives them.
 * Plain words and dates; the details an operator needs are in the RUNBOOK.
 * S7: the messages not sent that are e-mails can be sent again from here.
 */
export function SystemScreen() {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const allowed = can(access, 'system.view')
  const system = useQuery({
    queryKey: SYSTEM_QUERY_KEY,
    queryFn: loadSystem,
    enabled: allowed,
    refetchInterval: SYSTEM_REFRESH_MS,
  })
  const copy = m.system
  const when = (iso: string | null) => (iso ? formatDateTime(locale, iso) : copy.never)
  const jobName = (name: string) =>
    (copy.worker.job as Readonly<Record<string, string>>)[name] ?? name

  return (
    <Guard allowed={allowed} title={copy.title}>
      <PageHeader
        title={copy.title}
        status={
          system.data ? (
            <StatusChip tone={system.data.summary === 'ok' ? 'success' : 'warning'}>
              {copy.state[system.data.summary]}
            </StatusChip>
          ) : null
        }
        intro={copy.intro}
        meta={
          system.data
            ? format(copy.checkedAt, { time: formatTime(locale, system.data.checkedAt) })
            : null
        }
      />
      <LoadState data={system.data} problem={problemOf(system.error)}>
        {(s) => (
          <>
            {s.failing.length > 0 ? (
              <Notice tone="danger" title={copy.failingTitle}>
                <ul className="flex list-disc flex-col gap-1 ps-6">
                  {s.failing.map((part) => (
                    <li key={part}>{copy.failing[part]}</li>
                  ))}
                </ul>
              </Notice>
            ) : null}

            <div className="grid gap-6 md:grid-cols-2">
              <Section title={copy.alerts.title}>
                {s.alerts.firing.length === 0 ? (
                  <p className="text-text-muted">{copy.alerts.none}</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {s.alerts.firing.map((alert) => (
                      <li key={alert.key}>
                        <p className="font-medium">
                          {isAlertKey(alert.key) ? m.alerts[alert.key].title : alert.key}
                        </p>
                        <p className="text-sm text-text-muted">
                          {format(copy.alerts.since, { when: when(alert.since) })}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-sm">
                  {s.alerts.recipients.it + s.alerts.recipients.en === 0
                    ? copy.alerts.noRecipients
                    : format(copy.alerts.recipients, {
                        it: s.alerts.recipients.it,
                        en: s.alerts.recipients.en,
                      })}
                </p>
              </Section>

              <NotificationsSection notifications={s.notifications} />

              <Section
                title={copy.backups.title}
                status={s.backups.mode === 'off' ? <PendingBadge /> : null}
              >
                {s.backups.mode === 'off' ? (
                  <p>{copy.backups.pending}</p>
                ) : (
                  <Facts
                    items={[
                      [copy.backups.base, when(s.backups.newestBase)],
                      [
                        copy.backups.verify,
                        s.backups.newestVerify
                          ? `${when(s.backups.newestVerify.at)}${s.backups.newestVerify.ok ? '' : ` · ${copy.backups.verifyFailed}`}`
                          : copy.never,
                      ],
                      [
                        copy.backups.files,
                        s.backups.newestFiles
                          ? `${when(s.backups.newestFiles.at)} · ${format(
                              copy.backups.filesCounts,
                              {
                                copied: s.backups.newestFiles.copied,
                                failed: s.backups.newestFiles.failed,
                                total: s.backups.newestFiles.total,
                              },
                            )}`
                          : copy.never,
                      ],
                      [
                        copy.backups.archiver,
                        s.backups.archiver.failing
                          ? format(copy.backups.archiverFailing, {
                              count: s.backups.archiver.failedCount,
                              when: when(s.backups.archiver.lastFailedAt),
                            })
                          : copy.backups.archiverOk,
                      ],
                      [
                        copy.backups.heartbeat,
                        `${when(s.backups.heartbeat)}${s.backups.busy === 'base' || s.backups.busy === 'files' ? ` · ${copy.backups.busy[s.backups.busy]}` : ''}`,
                      ],
                    ]}
                  />
                )}
              </Section>

              <Section title={copy.worker.title}>
                <Facts items={[[copy.worker.heartbeat, when(s.worker.heartbeat)]]} />
                {s.worker.jobs.length > 0 ? (
                  <>
                    <h3 className="text-lg">{copy.worker.jobs}</h3>
                    <ul className="flex flex-col gap-2">
                      {s.worker.jobs.map((job) => (
                        <li key={job.name}>
                          <p className="font-medium">{jobName(job.name)}</p>
                          <p className="text-sm text-text-muted">
                            {format(copy.worker.lastSuccess, { when: when(job.lastSuccessAt) })}
                          </p>
                          {job.consecutiveFailures > 0 ? (
                            <p className="text-sm text-danger">
                              {format(copy.worker.failures, { count: job.consecutiveFailures })}
                            </p>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
                <h3 className="text-lg">{copy.daily.title}</h3>
                <DailyLine
                  testId="daily-morning"
                  title={copy.daily.morning}
                  job={s.daily.morning}
                  last={s.daily.lastMorning}
                />
                <DailyLine
                  testId="daily-reconcile"
                  title={copy.daily.reconcile}
                  job={s.daily.reconcile}
                  last={s.daily.lastReconcile}
                />
              </Section>

              <Section
                title={copy.reporting.title}
                status={s.reporting.state === 'pending' ? <PendingBadge /> : null}
              >
                <p>
                  {s.reporting.state === 'on'
                    ? format(copy.reporting.on, {
                        sent: s.reporting.sent,
                        dropped: s.reporting.dropped,
                      })
                    : copy.reporting.pending}
                </p>
                {s.reporting.sessions === 'refused' ? (
                  <p className="text-sm">{copy.reporting.sessionsRefused}</p>
                ) : null}
                <Facts
                  items={[
                    [
                      copy.disk.title,
                      s.disk
                        ? format(copy.disk.free, { percent: s.disk.freePercent })
                        : copy.disk.unknown,
                    ],
                  ]}
                />
              </Section>

              <ComplianceSections system={s} />

              <PrivacySection privacy={s.privacy} />

              <OutboxSection outbox={s.outbox} />
            </div>

            {/* C5: versions, fingerprints and raw errors, for the Owner when asked. */}
            <TechnicalDetails>
              <p>
                {format(copy.releases, {
                  api: s.releases.api,
                  worker: s.releases.worker ?? copy.never,
                })}
              </p>
              <p>
                {format(copy.compliance.codesHash, {
                  hash: s.compliance.cargos.codes.sha256.slice(0, 12),
                })}
              </p>
              {s.worker.jobs
                .filter((job) => job.consecutiveFailures > 0 && job.lastError)
                .map((job) => (
                  <p key={job.name} className="break-words">
                    {format(copy.worker.lastError, {
                      job: jobName(job.name),
                      error: job.lastError ?? '',
                    })}
                  </p>
                ))}
              {s.outbox.deadLetters.length > 0 ? (
                <>
                  <h3 className="text-lg">{copy.outbox.deadTitle}</h3>
                  <DataTable
                    label={copy.outbox.deadTitle}
                    columns={[
                      { header: copy.outbox.topic, cell: (dead) => dead.topic, rowHeader: true },
                      { header: copy.outbox.attempts, cell: (dead) => dead.attempts },
                      { header: copy.outbox.when, cell: (dead) => when(dead.deadAt) },
                      {
                        header: copy.outbox.error,
                        cell: (dead) => dead.error ?? '',
                        className: 'break-words',
                      },
                    ]}
                    rows={s.outbox.deadLetters}
                    rowKey={(dead) => dead.id}
                  />
                </>
              ) : null}
            </TechnicalDetails>
          </>
        )}
      </LoadState>
    </Guard>
  )
}

type Outbox = ApiSchemas['StaffSystem']['outbox']
type DeadLetter = Outbox['deadLetters'][number]

/**
 * S2's counts, and S7's e-mails not sent, in words: what each is, the booking
 * it is about and how late it is, with its way back into the queue. The
 * outcome is said above the list, since the row it is about leaves the list
 * once queued again (the Interventions rule, UI-2g). The raw rows stay under
 * Dettagli tecnici (C5).
 */
function OutboxSection({ outbox }: { outbox: Outbox }) {
  const { locale, messages: m } = useLocale()
  const copy = m.system.outbox
  const [resent, setResent] = useState<string | null>(null)
  const kind = (topic: string) => (copy.kinds as Readonly<Record<string, string>>)[topic] ?? topic
  const emails = outbox.deadLetters.filter((dead) => dead.resendable)
  return (
    <Section title={copy.title} className="md:col-span-2">
      {resent ? (
        <OutcomeNotice key={resent}>
          <p>{copy.resend.done}</p>
        </OutcomeNotice>
      ) : null}
      <Facts
        items={[
          [copy.pending, String(outbox.pending)],
          [
            copy.oldest,
            outbox.oldestDueAt ? formatDateTime(locale, outbox.oldestDueAt) : m.system.never,
          ],
          [copy.retrying, String(outbox.retrying)],
          [copy.dead, String(outbox.dead)],
        ]}
      />
      {outbox.deadLetters.length === 0 ? <p className="text-text-muted">{copy.noneDead}</p> : null}
      {emails.length > 0 ? (
        <>
          <h3 className="text-lg">{copy.emails.title}</h3>
          <p className="text-sm text-text-muted">{copy.emails.intro}</p>
          <ul className="flex flex-col gap-4">
            {emails.map((dead) => (
              <li key={dead.id} className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-1">
                  <p className="font-medium">
                    {kind(dead.topic)}
                    {dead.code ? (
                      <>
                        {' '}
                        <BookingCode code={dead.code} />
                      </>
                    ) : null}
                  </p>
                  <p className="text-sm text-text-muted">
                    {format(copy.emails.line, {
                      queued: formatDateTime(locale, dead.createdAt),
                      attempts: dead.attempts,
                    })}
                  </p>
                </div>
                <ResendAction
                  dead={dead}
                  what={dead.code ? `${kind(dead.topic)} ${dead.code}` : kind(dead.topic)}
                  onResent={() => setResent(dead.id)}
                />
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {outbox.deadLetters.length > emails.length ? (
        <p className="text-sm">{copy.emails.others}</p>
      ) : null}
    </Section>
  )
}

/**
 * S7: one e-mail not sent, back in the queue after a question that names it
 * and says how late it would arrive. The server reads its facts again when it
 * leaves, so the outcome says "queued", never "sent".
 */
function ResendAction({
  dead,
  what,
  onResent,
}: {
  dead: DeadLetter
  what: string
  onResent: () => void
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.system.outbox.resend
  const queryClient = useQueryClient()
  const dialog = useRef<HTMLDialogElement>(null)
  const { send, busy, problem, clear } = useSend()

  async function confirm() {
    const answer = await send(() =>
      call(
        api.POST('/v1/staff/system/dead-letters/{id}/resend', {
          params: { path: { id: dead.id } },
        }),
      ),
    )
    if (!answer) return
    dialog.current?.close()
    // Said before the row goes, so the focus has somewhere to land (UI-2g).
    onResent()
    await queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEY })
  }

  return (
    <>
      <Button
        variant="outline"
        className="rounded-sm"
        aria-label={format(copy.label, { what })}
        onClick={() => {
          // A refusal from an earlier try is not this try's (OPS-1).
          clear()
          dialog.current?.showModal()
        }}
      >
        {copy.action}
      </Button>
      <ConfirmDialog
        dialogRef={dialog}
        title={copy.title}
        confirmLabel={copy.confirm}
        cancelLabel={copy.keep}
        busy={busy}
        onConfirm={() => void confirm()}
        onClose={clear}
      >
        <p className="font-medium">{what}</p>
        <p>{format(copy.queued, { when: formatDateTime(locale, dead.createdAt) })}</p>
        <p>{copy.recheck}</p>
        <ProblemNotice problem={problem} />
      </ConfirmDialog>
    </>
  )
}

type Daily = ApiSchemas['StaffSystem']['daily']

/** S5: a daily job: when it runs next, or why it cannot (PENDING), and how its latest went. */
function DailyLine({
  testId,
  title,
  job,
  last,
}: {
  testId: string
  title: string
  job: Daily['morning']
  last: Daily['lastMorning']
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.system.daily
  return (
    <div data-testid={testId}>
      <p className="font-medium">{title}</p>
      {/* D7: a job that cannot run yet wears the badge, like every pane of something not live. */}
      {job !== null && job.state !== 'scheduled' ? (
        <PendingLine>{copy.pending[job.reason]}</PendingLine>
      ) : (
        <p className="text-sm text-text-muted">
          {job === null
            ? copy.unknown
            : format(copy.next, { when: formatDateTime(locale, job.nextAt) })}
        </p>
      )}
      {last ? (
        <p className="text-sm text-text-muted">
          {format(copy.last, {
            date: formatDate(locale, last.date),
            status: copy.status[last.status],
          })}
        </p>
      ) : null}
    </div>
  )
}

/**
 * S7: the customer reminders and the staff alerts, each on with its timing
 * or off and why, and who reads the staff alerts. Without a mail server
 * nothing can be sent: the pane wears the badge (D7) and says so once.
 */
function NotificationsSection({
  notifications,
}: {
  notifications: ApiSchemas['StaffSystem']['notifications']
}) {
  const { messages: m } = useLocale()
  const copy = m.system.notifications
  const lines = noticeLines(notifications, copy)
  return (
    <Section title={copy.title} status={lines.noEmail ? <PendingBadge /> : null}>
      {lines.noEmail ? (
        <p>{copy.off.no_email}</p>
      ) : (
        <Facts
          items={[
            [copy.pickup, lines.pickup],
            [copy.return, lines.return],
            [copy.late, lines.late],
            [copy.alerts, lines.alerts],
          ]}
        />
      )}
      <p className="text-sm">{lines.recipients}</p>
    </Section>
  )
}

/** S6: CaRGOS as the API reaches it; the fiscal provider (F1: PENDING but in local and test runs). */
function ComplianceSections({ system }: { system: ApiSchemas['StaffSystem'] }) {
  const { locale, messages: m } = useLocale()
  const copy = m.system.compliance
  const { cargos, fiscal } = system.compliance
  // Each status as its own label with its count, as the chips on the hand-over say it.
  const counts =
    cargos.last24h.length === 0 ? (
      copy.none
    ) : (
      <span className="flex flex-wrap gap-x-4">
        {cargos.last24h.map((row) => (
          <span key={row.status}>
            {m.cargos.status[row.status]} <span className="figures">{row.count}</span>
          </span>
        ))}
      </span>
    )
  // F1: the provider's requests still without a document, each status with its count.
  const waiting =
    fiscal.waiting.length === 0 ? (
      copy.fiscalNoneWaiting
    ) : (
      <span className="flex flex-wrap gap-x-4">
        {fiscal.waiting.map((row) => (
          <span key={row.status}>
            {copy.fiscalStatus[row.status]} <span className="figures">{row.count}</span>
          </span>
        ))}
      </span>
    )
  return (
    <>
      <Section title={copy.cargos} status={cargos.port === 'pending' ? <PendingBadge /> : null}>
        <p>{cargos.enabled ? copy.port[cargos.port] : copy.off}</p>
        {cargos.stuck && cargos.oldestQueuedAt ? (
          <Notice tone="danger">
            <p>{format(copy.stuck, { when: formatDateTime(locale, cargos.oldestQueuedAt) })}</p>
          </Notice>
        ) : null}
        {cargos.codes.stale ? (
          <Notice tone="warning">
            <p>{format(copy.codesStale, { days: cargos.codes.ageDays })}</p>
          </Notice>
        ) : null}
        <Facts
          items={[
            [
              copy.codes,
              format(copy.codesValue, {
                date: formatDate(locale, cargos.codes.fetchedOn),
                places: cargos.codes.places,
              }),
            ],
            [copy.last24h, counts],
            ...(cargos.oldestQueuedAt
              ? [[copy.oldestQueued, formatDateTime(locale, cargos.oldestQueuedAt)] as const]
              : []),
          ]}
        />
      </Section>
      <Section title={copy.fiscal} status={fiscal.provider === 'pending' ? <PendingBadge /> : null}>
        <p>{fiscal.provider === 'pending' ? copy.fiscalPending : copy.fiscalFake}</p>
        <p>{copy.fiscalOnline[fiscal.onlinePaymentDocument]}</p>
        <Facts items={[[copy.fiscalWaiting, waiting]]} />
      </Section>
    </>
  )
}
